import React, { createContext, useContext, useState, useEffect } from 'react';
import analyticsService from '../services/analyticsService';
import safeStorage from '../utils/safeStorage';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartLoaded, setCartLoaded] = useState(false);

  useEffect(() => {
    console.log('🔍 [HYDRATION DEBUG] CartContext useEffect running');
    console.log('🔍 [HYDRATION DEBUG] Window available:', typeof window !== 'undefined');
    console.log('🔍 [HYDRATION DEBUG] LocalStorage available:', typeof localStorage !== 'undefined');
    
    // Load cart from localStorage on mount
    const savedCart = safeStorage.getItem('rainbowSails_cart');
    console.log('🔍 [HYDRATION DEBUG] Saved cart from localStorage:', savedCart);
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        console.log('🔍 [HYDRATION DEBUG] Parsed cart:', parsedCart);
        setCartItems(parsedCart);
      } catch (error) {
        console.error('Failed to parse saved cart:', error);
        safeStorage.removeItem('rainbowSails_cart');
      }
    }
    setCartLoaded(true); // Mark cart as loaded
    console.log('🔍 [HYDRATION DEBUG] Cart loaded, cartLoaded set to true');
  }, []);

  const addToCart = (product, quantity = 1) => {
    // Track add to cart event
    analyticsService.trackAddToCart(product, quantity);
    
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      
      if (existingItem) {
        const updatedItems = prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
        safeStorage.setItem('rainbowSails_cart', JSON.stringify(updatedItems));
        return updatedItems;
      } else {
        const newItems = [...prevItems, { ...product, quantity }];
        safeStorage.setItem('rainbowSails_cart', JSON.stringify(newItems));
        return newItems;
      }
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => {
      const removedItem = prevItems.find(item => item.id === productId);
      if (removedItem) {
        // Track remove from cart
        analyticsService.trackRemoveFromCart(removedItem, removedItem.quantity);
      }
      
      const newItems = prevItems.filter(item => item.id !== productId);
      safeStorage.setItem('rainbowSails_cart', JSON.stringify(newItems));
      return newItems;
    });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCartItems(prevItems => {
      const updatedItems = prevItems.map(item =>
        item.id === productId ? { ...item, quantity } : item
      );
      safeStorage.setItem('rainbowSails_cart', JSON.stringify(updatedItems));
      return updatedItems;
    });
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('rainbowSails_cart');
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const value = {
    cartItems,
    cartLoaded,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    console.warn('useCart used outside CartProvider - returning default values');
    return {
      cartItems: [],
      cartLoaded: false,
      addToCart: () => {},
      removeFromCart: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      getTotalItems: () => 0,
      getTotalPrice: () => 0
    };
  }
  return context;
};

export default CartContext;